import Api from '@/modules/core/api/api.ts';
import DocumentCard from '@/modules/document/models/card/document-card.ts';
import DocumentOption from '@/modules/common/models/option/document-option.ts';
import { DocumentEndPoint, ProjectEndPoint } from '@/modules/document/enums/document-end-point.enum.ts';
import { DocumentTypeAdapter } from '@/modules/document/adapters/document-type.adapter.ts';
import { DocumentStatusAdapter } from '@/modules/document/adapters/document-status.adapter.ts';
import { DocumentCheckIdAdapter } from '@/modules/document/adapters/document-check-id.adapter.ts';
import { RequestUtil } from '@/modules/core/utils/request.util.ts';
import { Direction } from '@/modules/common/enums/direction.enum.ts';
import type { ItemsDto } from '@/modules/common/dto/items.dto.ts';
import type { DocumentTypeDto } from '@/modules/document/dto/document-type.dto.ts';
import type { DocumentStatusDto } from '@/modules/document/dto/document-status.dto.ts';
import type { DocumentCheckIdDto } from '@/modules/document/dto/document-check-id.dto.ts';
import type { ColorSourceDto, DocumentColorDto } from '@/modules/document/dto/document-color.dto.ts';
import type {
  DocumentColorLockSource,
  DocumentColorSources,
  DocumentColorState,
  DocumentOwnColorState,
} from '@/modules/document/types/document-color.type.ts';
import type { ColorCode } from '@/modules/core/enums/color-code.enum.ts';
import { DEFAULT_DOCUMENT_COLOR_STATE } from '@/modules/document/consts/document-color.ts';
import { ErrorUtil } from '@/modules/common/utils/error.util.ts';
import { notification } from '@/modules/common/composables/use-notification.ts';
import { SystemApi } from '@/modules/core/api/system.api.ts';
import { TableId } from '@/modules/common/enums/table-id.enum.ts';
import type { DateTime } from 'luxon';
import { mapMaskFormValue } from '@/modules/document/document-master/utils/mask-value.ts';
import { getDeadlineDate, getDeadlineRequest } from '@/modules/common/utils/deadline.ts';
import type {
  DocumentAddLinkParams,
  DocumentAddParams,
  DocumentAddRouteParams,
} from '@/modules/document/types/document-master-api.type.ts';
import GatewayApi from '@/modules/core/api/gateway.api.ts';
import { GatewayFilter } from '@/modules/core/utils/gateway-filter.ts';
import type { DocumentFullDto } from '@/modules/document/dto/document.dto.ts';
import type { DocumentInternalDto } from '@/modules/document/dto/document-internal.dto.ts';
import type { DocumentControllerOptionDto } from '@/modules/document/dto/document-directory.dto.ts';
import type { DocumentAuditDto } from '@/modules/document/dto/document-audit.dto.ts';
import type { DocumentEditParams } from '@/modules/document/types/document-edit.type.ts';
import type {
  DocumentDirectoryRequest,
  DocumentEditRequest,
  DocumentSetRegistrationDataRequest,
} from '@/modules/document/requests/document-edit.request.ts';

export class DocumentApi {
  static readonly GET_HINTS_COUNT = 500;
  static readonly NO_USER_LOGIN_ID = -1;
  static readonly OWN_COLOR_SOURCE = 'документ';
  static readonly CONTROLLER_CHANGE_EVENT_ID = 141;

  private static getColorLockSource(source: string | null): DocumentColorLockSource | null {
    const normalized = source?.trim().toLowerCase();

    if (normalized === DocumentApi.OWN_COLOR_SOURCE) {
      return 'document';
    }

    if (normalized === 'проект') {
      return 'project';
    }

    if (normalized === 'тип' || normalized === 'тип документа') {
      return 'documentType';
    }

    return null;
  }

  static async getDocument(id: number, signal?: AbortSignal) {
    const response = await GatewayApi.get<DocumentFullDto>(DocumentEndPoint.GetList, 'DOC_ID', id, signal);
    return new DocumentCard(response);
  }

  static async getColorState(id: number, signal?: AbortSignal) {
    const states = await DocumentApi.getColorStates([id], signal);
    return states.get(id) ?? DEFAULT_DOCUMENT_COLOR_STATE;
  }

  static async getColorStates(ids: number[], signal?: AbortSignal) {
    if (!ids.length) {
      return new Map<number, DocumentColorState>();
    }

    const response = await GatewayApi.execute<ItemsDto<DocumentColorDto>>(DocumentEndPoint.GetColor, {
      filter: GatewayFilter.and(GatewayFilter.in('DOC_ID', ids)),
      limit: ids.length,
      signal,
    });

    return new Map<number, DocumentColorState>(
      response.items.map((item) => {
        const isLocked = !!item.БлокЦвета;
        const lockSource = isLocked ? DocumentApi.getColorLockSource(item.ЦветИсточник) : null;

        return [
          item.DOC_ID,
          {
            color: item.Цвет,
            isLocked,
            isLockedByDocument: lockSource === 'document',
            lockSource,
          },
        ];
      })
    );
  }

  static async getOwnColorState(id: number, signal?: AbortSignal): Promise<DocumentOwnColorState> {
    const response: ItemsDto<DocumentColorDto> = await Api.post(
      DocumentEndPoint.GetColor,
      { doc_id: id, login_id: DocumentApi.NO_USER_LOGIN_ID },
      signal
    );
    const item = response.items[0];
    const isOwn = DocumentApi.getColorLockSource(item?.ЦветИсточник ?? null) === 'document';
    const isLockedBySource = !isOwn && !!item?.БлокЦвета;
    const lockSource = isLockedBySource ? DocumentApi.getColorLockSource(item.ЦветИсточник) : null;

    return {
      color: isOwn ? item.Цвет : null,
      isLocked: isOwn && !!item.БлокЦвета,
      isLockedBySource,
      sourceColor: !isOwn ? (item?.Цвет ?? null) : null,
      lockSource: lockSource === 'document' ? null : lockSource,
    };
  }

  static async getColorSources(signal?: AbortSignal): Promise<DocumentColorSources> {
    const [types, projects]: [
      ItemsDto<ColorSourceDto & { DT_ID: number }>,
      ItemsDto<ColorSourceDto & { P_ID: number }>,
    ] = await Promise.all([
      Api.post(DocumentEndPoint.GetAllTypes, {}, signal),
      Api.post(ProjectEndPoint.GetList, {}, signal),
    ]);

    return {
      types: DocumentApi.collectSourceColors(types.items.map((item) => [item.DT_ID, item])),
      projects: DocumentApi.collectSourceColors(projects.items.map((item) => [item.P_ID, item])),
    };
  }

  private static collectSourceColors(items: [number, ColorSourceDto][]) {
    return Object.fromEntries(items.map(([id, item]) => [id, { color: item.Цвет, isLocked: !!item.БлокЦвета }]));
  }

  static setColor(id: number, color: ColorCode) {
    return DocumentApi.postColor(DocumentEndPoint.SetColor, {
      PARENT_TABLE_ID: TableId.Documents,
      PARENT_ID: id,
      Цвет: color,
    });
  }

  static unsetColor(id: number) {
    return DocumentApi.postColor(DocumentEndPoint.UnsetColor, {
      PARENT_TABLE_ID: TableId.Documents,
      PARENT_ID: id,
    });
  }

  private static async postColor(endPoint: DocumentEndPoint, request: Record<string, unknown>) {
    try {
      await Api.post(endPoint, request);
    } catch (error) {
      if (ErrorUtil.isUnexpected(error)) {
        notification.error('document.error.setColor', error);
      }

      throw error;
    }
  }

  static async getOptions(query: string, signal?: AbortSignal): Promise<DocumentOption[]> {
    const response = await Api.post(DocumentEndPoint.GetOptions, { query }, signal);
    return response.items.map((item: Record<string, unknown>) => new DocumentOption(item, 'Доступен'));
  }

  static async getLinkedDocumentIds(documentId: number, signal?: AbortSignal): Promise<Set<number>> {
    const response = await Api.post(DocumentEndPoint.GetLinks, { doc_id: documentId }, signal);
    return new Set(response.items.map((item: Record<string, unknown>) => Number(item['Номер'])));
  }

  static async getLinkedClientIds(documentId: number, signal?: AbortSignal): Promise<Set<number>> {
    const response = await Api.post(DocumentEndPoint.GetClients, { doc_id: documentId }, signal);
    return new Set(response.items.map((item: Record<string, unknown>) => Number(item['CLIENT_ID'])));
  }

  static async getTypes(signal: AbortSignal) {
    const response: ItemsDto<DocumentTypeDto> = await Api.post(
      DocumentEndPoint.GetTypes,
      RequestUtil.orderBy('Название', Direction.Asc),
      signal
    );
    return DocumentTypeAdapter.mapList(response);
  }

  static async getStatuses(signal: AbortSignal) {
    const response: ItemsDto<DocumentStatusDto> = await Api.post(
      DocumentEndPoint.GetStatuses,
      RequestUtil.orderBy('Статус', Direction.Asc),
      signal
    );
    return DocumentStatusAdapter.mapList(response);
  }

  static async start(id: number) {
    try {
      return await Api.post(DocumentEndPoint.SetActivation, { doc_id: id, active: 1 });
    } catch (error) {
      // TODO: Выводить нотификацию автоматически в глобальном обработчике
      if (ErrorUtil.isUnexpected(error)) {
        notification.error('document.error.start', error);
      }

      throw error;
    }
  }

  static async read(id: number) {
    try {
      await Api.post(DocumentEndPoint.SetRead, { doc_id: id });
    } catch (error) {
      // TODO: Выводить нотификацию автоматически в глобальном обработчике
      if (ErrorUtil.isUnexpected(error)) {
        notification.error('document.error.read', error);
      }

      throw error;
    }
  }

  static unread(id: number) {
    return SystemApi.unread(id, TableId.Documents);
  }

  static readAll() {
    return SystemApi.readAll(TableId.Documents);
  }

  static async pause(id: number) {
    try {
      return await Api.post(DocumentEndPoint.SetActivation, { doc_id: id, active: 0 });
    } catch (error) {
      // TODO: Выводить нотификацию автоматически в глобальном обработчике
      if (ErrorUtil.isUnexpected(error)) {
        notification.error('document.error.pause', error);
      }

      throw error;
    }
  }

  static setController(documentId: number, loginId: number, signal?: AbortSignal) {
    return Api.post(DocumentEndPoint.SetController, { doc_id: documentId, login_id: loginId }, signal);
  }

  static setPreviousController(documentId: number, signal?: AbortSignal) {
    return Api.post(DocumentEndPoint.SetPreviousController, { doc_id: documentId }, signal);
  }

  static register(documentId: number, regNumber: string, regDate: string, signal?: AbortSignal) {
    return Api.post(
      DocumentEndPoint.Register,
      { doc_id: documentId, reg_number: regNumber, reg_date: regDate },
      signal
    );
  }

  static setStartDate(documentId: number, date: DateTime, signal?: AbortSignal) {
    return Api.post(DocumentEndPoint.SetStartDate, { doc_id: documentId, start_date: date.toSQLDate() }, signal);
  }

  static unsetStartDate(documentId: number, signal?: AbortSignal) {
    return Api.post(DocumentEndPoint.UnsetStartDate, { doc_id: documentId }, signal);
  }

  static async add(parameters: DocumentAddParams, signal?: AbortSignal) {
    const mask = parameters.mask.parameters.map(({ number }) => mapMaskFormValue(parameters.mask.formData[number]));

    const response = await Api.post(
      DocumentEndPoint.Add,
      {
        dt_id: parameters.typeId,
        p_id: parameters.projectId,
        controller_id: parameters.controllerId,
        content: parameters.content,
        comment: parameters.comment,
        plan_end_date: getDeadlineDate(parameters.deadline),
        mask_params: mask,
      },
      signal
    );

    return response.doc_id as number;
  }

  static addLink(parameters: DocumentAddLinkParams, signal?: AbortSignal) {
    return Api.post(
      DocumentEndPoint.AddLink,
      {
        parent_table_id: parameters.tableId,
        doc_id: parameters.documentId,
        parent_id: parameters.id,
        lt_id: parameters.typeId,
      },
      signal
    );
  }

  static async addLinks(parameters: DocumentAddLinkParams[], signal?: AbortSignal) {
    if (!parameters.length) {
      return;
    }

    return Api.post(
      DocumentEndPoint.AddLinks,
      {
        links: parameters.map((link) => ({
          parent_table_id: link.tableId,
          doc_id: link.documentId,
          parent_id: link.id,
          lt_id: link.typeId,
        })),
      },
      signal
    );
  }

  static deleteLink(linkId: number, signal?: AbortSignal) {
    return Api.post(DocumentEndPoint.DeleteLink, { link_id: linkId }, signal);
  }

  static addRoute(parameters: DocumentAddRouteParams, signal?: AbortSignal) {
    const routePointsResponsibles = (parameters.contractors ?? [])
      .filter((contractor) => {
        if (!contractor.contractor && !parameters.skipUnassigned) {
          throw new Error('The contractor is not defined.');
        }

        return contractor.contractor !== null;
      })
      .map((contractor) => ({
        RESPONSIBLE_TABLE_ID: contractor.contractor?.tableId,
        RESPONSIBLE_ID: contractor.contractor?.id,
        ...getDeadlineRequest(contractor.deadline),
        description: contractor.description,
        comment: contractor.unspecified.comment,
      }));

    return Api.post(
      DocumentEndPoint.AddRoute,
      {
        DOC_ID: parameters.documentId,
        PARENT_DOC_ID: parameters.parentDocumentId,
        RP_ID: parameters.routePointsIds,
        routePointsResponsibles,
      },
      signal
    );
  }

  static stop(documentId: number, comment: string, signal?: AbortSignal) {
    return Api.post(DocumentEndPoint.Stop, { doc_id: documentId, comment }, signal);
  }

  static async checkById(id: number, signal: AbortSignal) {
    const response: ItemsDto<DocumentCheckIdDto> = await Api.post(DocumentEndPoint.CheckById, { doc_id: id }, signal);
    return DocumentCheckIdAdapter.mapList(response);
  }

  static getInternal(id: number, signal?: AbortSignal) {
    return GatewayApi.get<DocumentInternalDto>(DocumentEndPoint.GetInternal, 'DOC_ID', id, signal);
  }

  static async edit(parameters: DocumentEditParams, signal?: AbortSignal) {
    const request: DocumentEditRequest = {
      doc_id: parameters.documentId,
      dt_id: parameters.typeId,
      p_id: parameters.projectId,
      controller_id: parameters.controllerId,
      content: parameters.content,
      comment: parameters.comment,
      plan_end_date: parameters.deadline?.toSQLDate() ?? null,
      start: parameters.startDate?.toSQLDate() ?? null,
      reg_date: parameters.registration.date?.toSQLDate() ?? null,
      reg_number: parameters.registration.number,
      color: parameters.color,
      color_is_locked: parameters.colorLocked,
    };

    if (parameters.mask) {
      const { parameters: maskParameters, formData } = parameters.mask;
      request.mask_params = maskParameters.map(({ number }) => mapMaskFormValue(formData[number]));
    }

    await Api.post(DocumentEndPoint.Edit, { ...request }, signal);
  }

  static async setRegistrationData(documentId: number, number: string, date: DateTime, signal?: AbortSignal) {
    await Api.post(
      DocumentEndPoint.SetRegistrationData,
      {
        doc_id: documentId,
        reg_number: number,
        reg_date: date.toFormat('yyyy-MM-dd HH:mm:ss'),
      } satisfies DocumentSetRegistrationDataRequest,
      signal
    );
  }

  static async getAvailableControllers(documentId: number, signal?: AbortSignal) {
    const response: ItemsDto<DocumentControllerOptionDto> = await Api.post(
      DocumentEndPoint.GetAvailableControllers,
      { doc_id: documentId } satisfies DocumentDirectoryRequest,
      signal
    );
    return response.items;
  }

  static async getPreviousControllerName(documentId: number, signal?: AbortSignal) {
    const audit = await GatewayApi.getOne<DocumentAuditDto>(
      DocumentEndPoint.GetAudit,
      GatewayFilter.and(GatewayFilter.eq('ET_ID', DocumentApi.CONTROLLER_CHANGE_EVENT_ID))!,
      {
        params: { doc_id: documentId },
        orderBy: [{ field: 'AUDIT_ID', direction: Direction.Desc }],
        signal,
      }
    );

    return audit?.Параметр01?.match(/\/(.+)\/\s*$/)?.[1].trim() || null;
  }
}
